/* eslint-disable react-hooks/exhaustive-deps */
import { tokens } from "../../../../theme";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  useGetLabelsToRefresh,
  useGetTenantServices,
} from "../../../../api/envs";
import useResizableDrawerWidth from "../../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../../components/DrawerSnapToggle";
import { refreshDrawerStyles } from "./styles";
import {
  DEFAULT_SCOPE,
  EMPTY_FILTER,
  MODES,
  SCOPE_FLAGS,
  branchBlockedReason,
  buildGroups,
  buildPayload,
  cloneRows,
  countInScope,
  countOverrides,
  countRows,
  emptyExtra,
  enabledScopeCount,
  plural,
} from "./helpers";
import StatusChip from "../../../../components/StatusChip";
import ConvoyDialog from "../../../../components/ConvoyDialog";
import StandardPanel from "./StandardPanel";
import UiPanel from "./UiPanel";
import CustomPanel from "./CustomPanel";
import ReviewPanel from "./ReviewPanel";
const TABS = [
  {
    mode: MODES.STANDARD,
    label: "Standard refresh",
    hint: "One click, release defaults",
  },
  { mode: MODES.UI, label: "UI only", hint: "Fastest \u2014 UI bundle only" },
  {
    mode: MODES.CUSTOM,
    label: "Custom refresh",
    hint: "Per-service overrides",
  },
];

const STEPS = { FORM: "form", REVIEW: "review" };

const PRIMARY_LABEL = {
  [MODES.STANDARD]: "Refresh environment",
  [MODES.UI]: "Refresh UI",
  [MODES.CUSTOM]: "Review",
};

// `refreshState` is the page's useRefreshEnv hook, so the drawer can show the
// outcome of the POST it just fired without owning the request.
const RefreshDrawer = ({
  refreshDialog,
  setRefreshDialog,
  refreshEnv,
  refreshState,
}) => {
  const theme = useTheme();
  const { refreshDrawer } = refreshDrawerStyles(tokens(theme.paletteId));

  const env = refreshDialog?.selectedEnv;
  const isOpen = Boolean(refreshDialog?.isOpen);
  const isSubmitting = Boolean(refreshState?.isLoading);

  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: drawerWidth,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "refresh-environment",
    defaultWidth: 720,
    maxWidth: 1200,
    enabled: isDesktopDrawer,
  });

  const [mode, setMode] = useState(MODES.STANDARD);
  const [step, setStep] = useState(STEPS.FORM);
  const [confirmClose, setConfirmClose] = useState(false);
  const [releaseLabel, setReleaseLabel] = useState("");
  const [labels, setLabels] = useState([]);
  const [scope, setScope] = useState(DEFAULT_SCOPE);
  const [baseConfigBranch, setBaseConfigBranch] = useState("");
  const [uiTag, setUiTag] = useState("");
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [edits, setEdits] = useState({});
  const [extras, setExtras] = useState({});
  const [expanded, setExpanded] = useState({});

  const { postData: getLabels, response: labelsResp } = useGetLabelsToRefresh();
  const {
    postData: getTenantServices,
    response: manifestResp,
    isLoading: isLoadingManifest,
  } = useGetTenantServices();

  const groups = useMemo(() => buildGroups(manifestResp), [manifestResp]);
  const overrideCount = useMemo(
    () => countOverrides(groups, edits, extras),
    [groups, edits, extras]
  );
  const totalCount = useMemo(() => countRows(groups), [groups]);
  const inScopeCount = useMemo(
    () => countInScope(groups, filter),
    [groups, filter]
  );
  const groupCount = (group) =>
    groups.find((g) => g.group === group)?.rows.length ?? 0;

  useEffect(() => {
    if (!isOpen) return;
    setMode(MODES.STANDARD);
    setStep(STEPS.FORM);
    setConfirmClose(false);
    setReleaseLabel("");
    setLabels([]);
    setScope(DEFAULT_SCOPE);
    setBaseConfigBranch("");
    setUiTag("");
    setFilter(EMPTY_FILTER);
    setEdits({});
    setExtras({});
    getLabels(`?deploymentName=${env?.name}`);
  }, [isOpen, env?.name]);

  // Targets render in response order; the first one is the default selection.
  useEffect(() => {
    if (!Array.isArray(labelsResp?.response)) return;
    setLabels(labelsResp.response);
    setReleaseLabel((current) =>
      labelsResp.response.includes(current)
        ? current
        : labelsResp.response[0] ?? ""
    );
  }, [labelsResp]);

  // The manifest drives the row counts in every footer, so it loads with the
  // label rather than waiting for the custom tab.
  useEffect(() => {
    if (isOpen && releaseLabel) getTenantServices(`/${releaseLabel}`);
  }, [isOpen, releaseLabel]);

  // A new manifest invalidates both the edits and the hand-added rows, since
  // their registry defaults come from the groups that just changed.
  useEffect(() => {
    if (!groups.length) return;
    setEdits(cloneRows(groups));
    setExtras(Object.fromEntries(groups.map(({ group }) => [group, []])));
    setExpanded(Object.fromEntries(groups.map(({ group }) => [group, true])));
  }, [groups]);

  const closeDrawer = () =>
    setRefreshDialog({ selectedEnv: {}, isOpen: false });

  // Overrides are the only state worth a confirmation; everything else is a
  // couple of clicks to recreate.
  const handleClose = () => {
    if (overrideCount > 0) {
      setConfirmClose(true);
      return;
    }
    closeDrawer();
  };

  const handleEdit = (group, key, field, value) =>
    setEdits((prev) => ({
      ...prev,
      [group]: {
        ...prev[group],
        [key]: { ...prev[group]?.[key], [field]: value },
      },
    }));

  const handleAddExtra = (group) =>
    setExtras((prev) => ({
      ...prev,
      [group]: [emptyExtra(group), ...(prev[group] ?? [])],
    }));

  const handleEditExtra = (group, idx, field, value) =>
    setExtras((prev) => ({
      ...prev,
      [group]: (prev[group] ?? []).map((extra, i) =>
        i === idx ? { ...extra, [field]: value } : extra
      ),
    }));

  const handleRemoveExtra = (group, idx) =>
    setExtras((prev) => ({
      ...prev,
      [group]: (prev[group] ?? []).filter((_, i) => i !== idx),
    }));

  const blockedReason = () => {
    if (mode === MODES.UI) return "";
    if (!releaseLabel) return "Pick a release target to continue";
    if (enabledScopeCount(scope) === 0) return "Select at least one scope";
    return branchBlockedReason(scope, baseConfigBranch);
  };

  const reason = blockedReason();
  const isReview = step === STEPS.REVIEW;

  // Close the drawer only after the API returns a success.
  useEffect(() => {
    if (!isOpen || refreshState?.isLoading !== false) return;
    const status = refreshState?.responseStatus;
    if (typeof status === "number" && status >= 200 && status < 300) {
      closeDrawer();
    }
  }, [isOpen, refreshState?.isLoading, refreshState?.responseStatus]);

  const submit = () => {
    refreshEnv(
      buildPayload({
        mode,
        env,
        releaseLabel,
        scope,
        groups,
        edits,
        extras,
        uiTag,
        baseConfigBranch,
      }),
      env
    );
  };

  const handlePrimary = () => {
    if (mode === MODES.CUSTOM && step === STEPS.FORM) {
      setStep(STEPS.REVIEW);
      return;
    }
    submit();
  };

  const images = plural(groupCount("dockerImages"), "image");
  const charts = plural(groupCount("helmCharts"), "chart");
  const items = plural(
    isReview || mode === MODES.CUSTOM ? inScopeCount : totalCount,
    "item"
  );
  const overrides = plural(overrideCount, "override");

  const summary = () => {
    if (reason) return reason;
    if (isReview)
      return `${items} \u00b7 ${overrides} \u00b7 target ${releaseLabel}`;
    if (mode === MODES.UI)
      return `Refreshes the UI bundle on ${
        env?.name || "this environment"
      } to tag ${uiTag.trim()}`;
    if (mode === MODES.CUSTOM) return `${items} in scope \u00b7 ${overrides}`;
    return `Refreshes ${images} \u00b7 ${charts} to ${releaseLabel} \u00b7 ${enabledScopeCount(
      scope
    )} of ${SCOPE_FLAGS.length} scopes selected`;
  };

  const primaryLabel = isReview ? "Confirm refresh" : PRIMARY_LABEL[mode];

  const body = () => {
    if (isReview) {
      return (
        <ReviewPanel
          env={env}
          mode={mode}
          releaseLabel={releaseLabel}
          scope={scope}
          groups={groups}
          edits={edits}
          extras={extras}
          uiTag={uiTag}
          baseConfigBranch={baseConfigBranch}
          inScopeCount={inScopeCount}
          totalCount={totalCount}
        />
      );
    }
    if (mode === MODES.UI) return <UiPanel uiTag={uiTag} setUiTag={setUiTag} />;
    if (mode === MODES.CUSTOM) {
      return (
        <CustomPanel
          labels={labels}
          releaseLabel={releaseLabel}
          setReleaseLabel={setReleaseLabel}
          release={manifestResp?.release}
          groups={groups}
          edits={edits}
          extras={extras}
          filter={filter}
          setFilter={setFilter}
          inScopeCount={inScopeCount}
          expanded={expanded}
          setExpanded={setExpanded}
          scope={scope}
          setScope={setScope}
          baseConfigBranch={baseConfigBranch}
          setBaseConfigBranch={setBaseConfigBranch}
          isLoading={isLoadingManifest}
          onEdit={handleEdit}
          onAddExtra={handleAddExtra}
          onEditExtra={handleEditExtra}
          onRemoveExtra={handleRemoveExtra}
        />
      );
    }
    return (
      <StandardPanel
        env={env}
        labels={labels}
        releaseLabel={releaseLabel}
        setReleaseLabel={setReleaseLabel}
        scope={scope}
        setScope={setScope}
        baseConfigBranch={baseConfigBranch}
        setBaseConfigBranch={setBaseConfigBranch}
      />
    );
  };

  const activeTab = TABS.find((tab) => tab.mode === mode);

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: { ...refreshDrawer.paper(drawerWidth), overflow: "visible" },
        }}
        slotProps={{ backdrop: { sx: refreshDrawer.backdrop } }}
      >
        <DrawerResizeHandle
          {...resizeHandleProps}
          ariaLabel="Resize refresh environment drawer"
        />
        <DrawerSnapToggle
          enabled={isDesktopDrawer}
          isWide={isDrawerWide}
          onClick={toggleDrawerWide}
          ariaLabel="Expand refresh environment drawer to 75% width"
          collapseLabel="Collapse refresh environment drawer"
          sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
        />
        <Box sx={refreshDrawer.header}>
          <Box sx={refreshDrawer.headerTop}>
            <Box sx={refreshDrawer.headerText}>
              <Typography sx={refreshDrawer.title}>
                Refresh environment
              </Typography>
              <Box sx={refreshDrawer.metaRow}>
                <Typography component="span" sx={refreshDrawer.metaMono}>
                  {env?.name || "-"}
                </Typography>
                {env?.status ? (
                  <Typography component="span" sx={refreshDrawer.metaDot}>
                    &middot;
                  </Typography>
                ) : null}
                <StatusChip value={env?.status} />
                <Typography component="span" sx={refreshDrawer.metaDot}>
                  &middot;
                </Typography>
                <Typography component="span">Dev/QA</Typography>
                <Typography component="span" sx={refreshDrawer.metaDot}>
                  &middot;
                </Typography>
                <Typography component="span" sx={refreshDrawer.metaOwnerLabel}>
                  owner
                </Typography>
                <Typography component="span" sx={refreshDrawer.metaMono}>
                  {env?.createdBy || "-"}
                </Typography>
              </Box>
            </Box>
            <IconButton
              sx={refreshDrawer.closeButton}
              onClick={handleClose}
              aria-label="Close"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {step === STEPS.FORM ? (
            <Box sx={refreshDrawer.tabRow} role="tablist">
              {TABS.map(({ mode: tabMode, label }) => (
                <Button
                  key={tabMode}
                  role="tab"
                  aria-selected={mode === tabMode}
                  disableRipple
                  sx={refreshDrawer.tab(mode === tabMode)}
                  onClick={() => setMode(tabMode)}
                >
                  {label}
                </Button>
              ))}
              <Typography sx={refreshDrawer.tabHint}>
                {activeTab?.hint}
              </Typography>
            </Box>
          ) : (
            <Box sx={refreshDrawer.tabRowSpacer} />
          )}
        </Box>

        <Box sx={refreshDrawer.body}>{body()}</Box>

        <Box sx={refreshDrawer.footer}>
          <Typography sx={refreshDrawer.footerSummary}>{summary()}</Typography>
          <Box sx={refreshDrawer.footerActions}>
            {isReview ? (
              <Button
                variant="outlined"
                color="inherit"
                sx={refreshDrawer.secondaryButton}
                onClick={() => setStep(STEPS.FORM)}
              >
                Back
              </Button>
            ) : null}
            <>
              <Button
                variant="outlined"
                color="inherit"
                sx={refreshDrawer.secondaryButton}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={refreshDrawer.primaryButton}
                disabled={Boolean(reason) || isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : null
                }
                onClick={handlePrimary}
              >
                {primaryLabel}
              </Button>
            </>
          </Box>
        </Box>
      </Drawer>

      <ConvoyDialog
        open={confirmClose}
        title="Discard overrides?"
        confirmBtnText="Discard"
        cancelBtnText="Keep editing"
        handleDialogConfirm={() => {
          setConfirmClose(false);
          closeDrawer();
        }}
        resetDialog={() => setConfirmClose(false)}
      >
        <Typography component="span" variant="h5">
          {`${overrides} will be lost if you close now.`}
        </Typography>
      </ConvoyDialog>
    </>
  );
};

export default RefreshDrawer;
